import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import Button from '../UI/Button';
import Form from '../UI/Form';

const CalculatorContainer = styled(motion.div)`
  background-color: #fff;
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  padding: 30px 25px;
  margin: 30px auto;
  max-width: 650px;
  width: 100%;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  text-align: center;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 45px rgba(63, 81, 181, 0.15);
  }

  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  margin-bottom: 15px;
  color: #333;
  font-weight: 800;
  text-align: center;
  background: linear-gradient(135deg, #3f51b5, #5c6bc0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 1rem;
  margin-bottom: 25px;
  line-height: 1.5;
  text-align: center;
  max-width: 90%;
  margin-left: auto;
  margin-right: auto;
`;

const SliderContainer = styled.div`
  margin-bottom: 20px;
  padding: 0 10px;
`;

const SliderLabel = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  
  span {
    color: #666;
    font-size: 0.9rem;
    font-weight: 500;
  }
`;

const SliderValue = styled.div`
  font-size: 2.2rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 20px;
  margin-top: 15px;
  color: #3f51b5;
  background: linear-gradient(135deg, #3f51b5, #5c6bc0);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const StyledSlider = styled.input`
  -webkit-appearance: none;
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: #e0e0e0;
  outline: none;
  transition: background 0.3s ease, height 0.3s ease;
  cursor: pointer;
  
  &:hover {
    height: 10px;
    background: linear-gradient(to right, #3f51b5, #7986cb);
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #3f51b5;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 0 0 4px rgba(63, 81, 181, 0.2);
  }

  &::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #3f51b5;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 0 0 4px rgba(63, 81, 181, 0.2);
  }

  &::-webkit-slider-thumb:hover {
    box-shadow: 0 0 0 6px rgba(63, 81, 181, 0.3);
    transform: scale(1.2);
  }
  
  &::-moz-range-thumb:hover {
    box-shadow: 0 0 0 6px rgba(63, 81, 181, 0.3);
    transform: scale(1.2);
  }
`;

const PriceTable = styled.div`
  background: linear-gradient(to bottom, #f5f7ff, #eef1ff);
  border-radius: 10px;
  padding: 20px;
  margin: 0 auto 25px;
  max-width: 90%;
  box-shadow: 0 6px 15px rgba(63, 81, 181, 0.08);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    background: linear-gradient(to bottom, #f0f3ff, #e8ecff);
    transform: scale(1.02);
    box-shadow: 0 10px 20px rgba(63, 81, 181, 0.12);
  }
`;

const PriceRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${props => props.$isLast ? 'transparent' : 'rgba(63, 81, 181, 0.1)'};
  font-size: ${props => props.$isTotal ? '1.2rem' : '1rem'};
  font-weight: ${props => props.$isTotal ? '700' : '500'};
  color: ${props => props.$isDiscount ? '#f44336' : props.$isTotal ? '#3f51b5' : '#555'};
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateX(5px);
  }
`;

const OrderFormContainer = styled.div`
  margin-top: 25px;
  display: ${props => props.$isVisible ? 'block' : 'none'};
  background: linear-gradient(to bottom, #f9f9f9, #f5f5f5);
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.05);
`;

const FormTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 20px;
  color: #3f51b5;
  font-weight: 600;
  text-align: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`;

const FilmTypeSelect = styled.select`
  width: 100%;
  padding: 12px 15px;
  margin-bottom: 20px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  background-color: white;
  font-size: 1rem;
  color: #444;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%233f51b5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 15px center;
  background-size: 14px;
  transition: all 0.3s ease;
  cursor: pointer;
  text-align: center;
  text-align-last: center;
  font-weight: 500;

  &:hover {
    border-color: #3f51b5;
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(63, 81, 181, 0.15);
  }

  &:focus {
    outline: none;
    border-color: #3f51b5;
    box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.2);
  }

  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 0.9rem;
  }
`;

const Calculator = () => {
  const [quantity, setQuantity] = useState(10);
  const [filmType, setFilmType] = useState('primary1kg');
  const [showOrderForm, setShowOrderForm] = useState(false);
  const [basePrice, setBasePrice] = useState(310);
  const [discount, setDiscount] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  
  // Данные о типах пленок и их ценах на основе скриншота
  const filmTypes = [
    { id: 'primary1kg', name: 'Стрейч-пленка 1 кг, первичное сырье', basePrice: 310 },
    { id: 'primary2kg', name: 'Стрейч-пленка 2 кг, первичное сырье', basePrice: 550 },
    { id: 'secondary1kg', name: 'Стрейч-пленка 1 кг, вторичное сырье', basePrice: 275 },
    { id: 'secondary2kg', name: 'Стрейч-пленка 2 кг, вторичное сырье', basePrice: 530 },
    { id: 'micro05kg', name: 'Стрейч-пленка микро ролик 125 мм, 0,5 кг', basePrice: 180 },
    { id: 'mini1kg', name: 'Стрейч-пленка мини ролик 250 мм, 1 кг', basePrice: 330 },
    { id: 'whitemicro05kg', name: 'Стрейч-пленка белая микро ролик 125 мм, 0,5 кг', basePrice: 200 },
    { id: 'whitemini1kg', name: 'Стрейч-пленка белая, мини ролик 250 мм, 1 кг', basePrice: 360 }
  ];
  
  // Цены в зависимости от объема для разных типов пленок
  const priceListByType = {
    'primary1kg': [
      { min: 1, max: 9, price: 310 },
      { min: 10, max: 49, price: +(310 * 0.8).toFixed(2) }, // Скидка 20%
      { min: 50, max: 99, price: +(310 * 0.75).toFixed(2) }, // Скидка 25%
      { min: 100, max: 1000, price: +(310 * 0.7).toFixed(2) } // Скидка 30%
    ],
    'primary2kg': [
      { min: 1, max: 9, price: 550 },
      { min: 10, max: 49, price: +(550 * 0.8).toFixed(2) }, // Скидка 20%
      { min: 50, max: 99, price: +(550 * 0.75).toFixed(2) }, // Скидка 25%
      { min: 100, max: 1000, price: +(550 * 0.7).toFixed(2) } // Скидка 30%
    ],
    'secondary1kg': [
      { min: 1, max: 9, price: 275 },
      { min: 10, max: 49, price: +(275 * 0.8).toFixed(2) }, // Скидка 20%
      { min: 50, max: 99, price: +(275 * 0.75).toFixed(2) }, // Скидка 25%
      { min: 100, max: 1000, price: +(275 * 0.7).toFixed(2) } // Скидка 30%
    ],
    'secondary2kg': [
      { min: 1, max: 9, price: 530 },
      { min: 10, max: 49, price: +(530 * 0.8).toFixed(2) }, // Скидка 20%
      { min: 50, max: 99, price: +(530 * 0.75).toFixed(2) }, // Скидка 25%
      { min: 100, max: 1000, price: +(530 * 0.7).toFixed(2) } // Скидка 30%
    ],
    'micro05kg': [
      { min: 1, max: 9, price: 180 },
      { min: 10, max: 49, price: +(180 * 0.8).toFixed(2) }, // Скидка 20%
      { min: 50, max: 99, price: +(180 * 0.75).toFixed(2) }, // Скидка 25%
      { min: 100, max: 1000, price: +(180 * 0.7).toFixed(2) } // Скидка 30%
    ],
    'mini1kg': [
      { min: 1, max: 9, price: 330 },
      { min: 10, max: 49, price: +(330 * 0.8).toFixed(2) }, // Скидка 20%
      { min: 50, max: 99, price: +(330 * 0.75).toFixed(2) }, // Скидка 25%
      { min: 100, max: 1000, price: +(330 * 0.7).toFixed(2) } // Скидка 30%
    ],
    'whitemicro05kg': [
      { min: 1, max: 9, price: 200 },
      { min: 10, max: 49, price: +(200 * 0.8).toFixed(2) }, // Скидка 20%
      { min: 50, max: 99, price: +(200 * 0.75).toFixed(2) }, // Скидка 25%
      { min: 100, max: 1000, price: +(200 * 0.7).toFixed(2) } // Скидка 30%
    ],
    'whitemini1kg': [
      { min: 1, max: 9, price: 360 },
      { min: 10, max: 49, price: +(360 * 0.8).toFixed(2) }, // Скидка 20%
      { min: 50, max: 99, price: +(360 * 0.75).toFixed(2) }, // Скидка 25%
      { min: 100, max: 1000, price: +(360 * 0.7).toFixed(2) } // Скидка 30%
    ]
  };

  // Функция для обработки изменения типа пленки
  const handleFilmTypeChange = (e) => {
    setFilmType(e.target.value);
  };

  // Расчет цены и скидки при изменении количества или типа пленки
  useEffect(() => {
    const calculatePrice = () => {
      const selectedFilm = filmTypes.find(film => film.id === filmType);
      const originalPrice = selectedFilm.basePrice; // Начальная цена без скидки
      const currentPriceList = priceListByType[filmType];
      
      // Определяем цену за штуку в зависимости от количества
      let currentPrice = originalPrice;
      for (const range of currentPriceList) {
        if (quantity >= range.min && quantity <= range.max) {
          currentPrice = range.price;
          break;
        } else if (quantity > currentPriceList[currentPriceList.length - 1].max) {
          currentPrice = currentPriceList[currentPriceList.length - 1].price;
        }
      }
      
      // Рассчитываем процент скидки
      const discountPercent = Math.round((1 - currentPrice / originalPrice) * 100);
      
      setBasePrice(currentPrice);
      setDiscount(discountPercent);
      setTotalPrice(currentPrice * quantity);
    };
    
    calculatePrice();
  }, [quantity, filmType]);

  const handleOrderClick = () => {
    setShowOrderForm(true);
  };

  const handleSubmitOrder = (formData) => {
    // Здесь будет логика отправки заказа на бэкенд
    const selectedFilm = filmTypes.find(film => film.id === filmType);
    console.log('Заказ оформлен:', { ...formData, filmType: selectedFilm.name, quantity, totalPrice });
    // После отправки можно показать сообщение об успехе
    alert('Ваш заказ успешно оформлен! Мы свяжемся с вами в ближайшее время.');
    setShowOrderForm(false);
  };

  return (
    <CalculatorContainer
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.01 }}
    >
      <Title>Калькулятор Опта</Title>
      <Subtitle>Выберите тип пленки и количество рулонов для расчета индивидуальной цены. Чем больше объем, тем выгоднее цена! От 10 рулонов - скидка 20%, от 50 рулонов - скидка 25%, от 100 рулонов - скидка 30%!</Subtitle>
      
      {/* Выпадающий список с типами пленок */}
      <SliderContainer>
        <SliderLabel>
          <span>Выберите тип пленки:</span>
        </SliderLabel>
        <FilmTypeSelect value={filmType} onChange={handleFilmTypeChange}>
          {filmTypes.map(film => (
            <option key={film.id} value={film.id}>{film.name}</option>
          ))}
        </FilmTypeSelect>
      </SliderContainer>
      
      <SliderContainer>
        <SliderLabel>
          <span>1 рулон</span>
          <span>1000 рулонов</span>
        </SliderLabel>
        <StyledSlider 
          type="range" 
          min="1" 
          max="1000" 
          value={quantity} 
          onChange={(e) => setQuantity(parseInt(e.target.value))} 
        />
        <SliderValue>{quantity} рулонов</SliderValue>
      </SliderContainer>
      
      <PriceTable>
        <PriceRow>
          <span>Базовая цена:</span>
          <span>{filmTypes.find(film => film.id === filmType).basePrice} руб/рулон</span>
        </PriceRow>
        <PriceRow $isDiscount={true}>
          <span>Ваша скидка:</span>
          <span>{discount}%</span>
        </PriceRow>
        <PriceRow>
          <span>Цена со скидкой:</span>
          <span>{basePrice} руб/рулон</span>
        </PriceRow>
        <PriceRow $isTotal $isLast>
          <span>Итоговая стоимость:</span>
          <span>{totalPrice.toLocaleString()} руб</span>
        </PriceRow>
      </PriceTable>
      
      {!showOrderForm && (
        <ButtonContainer>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            onClick={handleOrderClick}
          >
            Оформить заказ
          </Button>
        </ButtonContainer>
      )}
      
      <OrderFormContainer $isVisible={showOrderForm}>
        <FormTitle>Заполните данные для оформления заказа</FormTitle>
        <Form 
          fields={[
            { name: 'name', label: 'Ваше имя', type: 'text', required: true },
            { name: 'phone', label: 'Телефон', type: 'tel', required: true },
            { name: 'email', label: 'E-mail', type: 'email', required: true },
            { name: 'comment', label: 'Комментарий к заказу', type: 'textarea' }
          ]}
          submitText="Отправить заказ"
          onSubmit={handleSubmitOrder}
          cancelText="Отмена"
          onCancel={() => setShowOrderForm(false)}
        />
      </OrderFormContainer>
    </CalculatorContainer>
  );
};

export default Calculator; 